// Geboortevenster · Stap 2 van de afleiding: activatieset → 21 eigenschapswaarden (met de derive-functies van de chart) → lanes met samengesmolten blokken.
// Overzicht van de feature: EgituraPlatform/docs/human-design/hd-birth-window.md

import { HdBodygraphConfig, HdBodygraphPlanetaryActivation, HdBodygraphRenderState } from '../bodygraph/hd-bodygraph.models';
import {
  deriveBasicProperties,
  deriveLifeCross,
  formatHdLifeCrossValue,
} from '../bodygraph/basic-properties/hd-chart-properties.util';
import { PhsCategory, phsCategoryFor } from '../bodygraph/hd-phs-metadata';
import { getHdPlanetDisplayConfig } from '../bodygraph/planet-grid/hd-planet-display-config';
import { toBirthWindowConfig } from './birth-window-activations.builder';
import {
  HD_BIRTH_WINDOW_PROPERTY_ORDER,
  HD_BIRTH_WINDOW_TIER_OF,
  HdBirthWindowBlock,
  HdBirthWindowEvaluate,
  HdBirthWindowInterval,
  HdBirthWindowLane,
  HdBirthWindowListDiff,
  HdBirthWindowPropertyKey,
  HdBirthWindowPropertyValue,
  HdBirthWindowSnapshot,
} from './birth-window.models';

/**
 * Van een activatieset naar de 17 eigenschapswaarden, en van intervallen naar
 * lanes met samengesmolten blokken (spec §2.4). Gebruikt exact dezelfde
 * afleidingen als de chart zelf (`deriveBasicProperties`, `deriveLifeCross`,
 * PHS-categorieën), zodat het venster nooit iets anders zegt dan het Basis-
 * en Geavanceerd-paneel.
 */

const SUN_PLANET_ID = getHdPlanetDisplayConfig('sun').planetId;
const NORTH_NODE_PLANET_ID = getHdPlanetDisplayConfig('northNode').planetId;

const NONE: HdBirthWindowPropertyValue = { id: 'none', payload: { kind: 'none' } };

export type HdBirthWindowRenderStateFactory = (configuration: HdBodygraphConfig) => HdBodygraphRenderState;

function find(
  activations: readonly HdBodygraphPlanetaryActivation[],
  planetId: number,
  side: 'personality' | 'design',
): HdBodygraphPlanetaryActivation | undefined {
  return activations.find(activation => activation.planetId === planetId && activation.side === side);
}

/** Toon 1-3 = links (L), 4-6 = rechts (R); 0 = onbekend. */
function arrow(tone: number | undefined): 'L' | 'R' | undefined {
  if (!tone || tone < 1 || tone > 6) return undefined;
  return tone <= 3 ? 'L' : 'R';
}

function phsValue(
  activation: HdBodygraphPlanetaryActivation | undefined,
  category: PhsCategory,
  part: 'color' | 'tone',
  idPrefix: string,
): HdBirthWindowPropertyValue {
  const color = activation?.color ?? 0;
  const tone = activation?.tone ?? 0;
  const relevant = part === 'color' ? color : tone;
  if (!activation || relevant < 1 || relevant > 6) return NONE;
  return {
    id: `${idPrefix}:${relevant}`,
    payload: { kind: 'phs', category, color, tone, part },
  };
}

function baseValue(
  activation: HdBodygraphPlanetaryActivation | undefined,
  category: PhsCategory,
  idPrefix: string,
): HdBirthWindowPropertyValue {
  const base = activation?.base ?? 0;
  if (!activation || base < 1 || base > 5) return NONE;
  return { id: `${idPrefix}:${base}`, payload: { kind: 'base', category, base } };
}

/** Maia-notatie van de vier pijlen: personality (Zon, Knoop) en design (Zon, Knoop), bv. "PLL DRR". */
export function formatBirthWindowVariable(activations: readonly HdBodygraphPlanetaryActivation[]): string | undefined {
  const pSun = arrow(find(activations, SUN_PLANET_ID, 'personality')?.tone);
  const pNode = arrow(find(activations, NORTH_NODE_PLANET_ID, 'personality')?.tone);
  const dSun = arrow(find(activations, SUN_PLANET_ID, 'design')?.tone);
  const dNode = arrow(find(activations, NORTH_NODE_PLANET_ID, 'design')?.tone);
  if (!pSun || !pNode || !dSun || !dNode) return undefined;
  return `P${pSun}${pNode} D${dSun}${dNode}`;
}

export function evaluateBirthWindowSnapshot(
  activations: readonly HdBodygraphPlanetaryActivation[],
  createRenderState: HdBirthWindowRenderStateFactory,
): HdBirthWindowSnapshot {
  const renderState = createRenderState(toBirthWindowConfig(activations));
  const basics = deriveBasicProperties(renderState, activations);
  const lifeCross = deriveLifeCross(activations);

  const type: HdBirthWindowPropertyValue = {
    id: `type:${basics.type.base}${basics.type.modifier ? ':m' : ''}${basics.type.pure ? ':p' : ''}`,
    payload: {
      kind: 'type',
      base: basics.type.base,
      manifesting: basics.type.modifier === 'manifesting',
      pure: basics.type.pure === true,
    },
  };
  const centers = [...basics.definedCenters].sort();
  const channels = [...basics.definedChannels].sort();
  const variableText = formatBirthWindowVariable(activations);
  const lifeCrossText = formatHdLifeCrossValue(lifeCross);

  const pSun = find(activations, SUN_PLANET_ID, 'personality');
  const dSun = find(activations, SUN_PLANET_ID, 'design');
  const pNode = find(activations, NORTH_NODE_PLANET_ID, 'personality');
  const dNode = find(activations, NORTH_NODE_PLANET_ID, 'design');

  return {
    type,
    authority: { id: `auth:${basics.authorityKey}`, payload: { kind: 'i18n', key: `authorityValues.${basics.authorityKey}` } },
    definition: { id: `def:${basics.definitionKey}`, payload: { kind: 'i18n', key: `definitionValues.${basics.definitionKey}` } },
    centers: { id: `cen:${centers.join(',')}`, payload: { kind: 'list', items: centers, itemKind: 'center' } },
    channels: { id: `ch:${channels.join(',')}`, payload: { kind: 'list', items: channels, itemKind: 'channel' } },
    profile: basics.profile
      ? {
        id: `prof:${basics.profile.personalityLine}/${basics.profile.designLine}`,
        payload: { kind: 'profile', personalityLine: basics.profile.personalityLine, designLine: basics.profile.designLine },
      }
      : NONE,
    incarnationCross: basics.incarnationCross
      ? {
        id: `cross:${basics.incarnationCross.angleKey}:${basics.incarnationCross.gates.join('-')}`,
        payload: {
          kind: 'cross',
          gates: basics.incarnationCross.gates,
          angleKey: basics.incarnationCross.angleKey,
          nameKey: basics.incarnationCross.name ?? undefined,
        },
      }
      : NONE,
    lifeCross: lifeCrossText ? { id: `life:${lifeCrossText}`, payload: { kind: 'text', text: lifeCrossText } } : NONE,
    variable: variableText ? { id: `var:${variableText}`, payload: { kind: 'text', text: variableText } } : NONE,
    determination: phsValue(dSun, phsCategoryFor('design', 'incarnation'), 'color', 'det'),
    cognition: phsValue(dSun, phsCategoryFor('design', 'incarnation'), 'tone', 'cog'),
    environment: phsValue(dNode, phsCategoryFor('design', 'life'), 'color', 'env'),
    environmentResonance: phsValue(dNode, phsCategoryFor('design', 'life'), 'tone', 'envt'),
    motivation: phsValue(pSun, phsCategoryFor('personality', 'incarnation'), 'color', 'mot'),
    sense: phsValue(pSun, phsCategoryFor('personality', 'incarnation'), 'tone', 'sen'),
    perspective: phsValue(pNode, phsCategoryFor('personality', 'life'), 'color', 'per'),
    perspectiveResonance: phsValue(pNode, phsCategoryFor('personality', 'life'), 'tone', 'pert'),
    determinationBase: baseValue(dSun, phsCategoryFor('design', 'incarnation'), 'detb'),
    environmentBase: baseValue(dNode, phsCategoryFor('design', 'life'), 'envb'),
    motivationBase: baseValue(pSun, phsCategoryFor('personality', 'incarnation'), 'motb'),
    perspectiveBase: baseValue(pNode, phsCategoryFor('personality', 'life'), 'perb'),
  };
}

/**
 * Handtekening van een activatieset zónder base: intervallen die enkel op
 * base verschillen (de Zon wisselt elke 7,6 min van base) delen hun dure
 * afleiding (`deriveBasicProperties`); enkel de vier base-lanes verschillen.
 */
function coreSignature(activations: readonly HdBodygraphPlanetaryActivation[]): string {
  return activations
    // `!` = geboorte ná de shift: zelfde cellen, andere kanaalmechaniek (19/49).
    .map(activation => `${activation.side}:${activation.planetId}:${activation.gate}.${activation.line}.${activation.color}.${activation.tone}${activation.postShiftBirth ? '!' : ''}`)
    .sort()
    .join('|');
}

type HdBirthWindowBaseKey = 'determinationBase' | 'environmentBase' | 'motivationBase' | 'perspectiveBase';

/** Evaluator met memo op de base-vrije handtekening (zie {@link coreSignature}). */
export function createMemoizedBirthWindowEvaluator(evaluate: HdBirthWindowEvaluate): HdBirthWindowEvaluate {
  const cache = new Map<string, HdBirthWindowSnapshot>();
  return activations => {
    const key = coreSignature(activations);
    const cached = cache.get(key);
    if (cached) {
      const fresh = evaluateBaseOnly(activations);
      return { ...cached, ...fresh };
    }
    const snapshot = evaluate(activations);
    cache.set(key, snapshot);
    return snapshot;
  };
}

function evaluateBaseOnly(activations: readonly HdBodygraphPlanetaryActivation[]): Pick<HdBirthWindowSnapshot, HdBirthWindowBaseKey> {
  const pSun = find(activations, SUN_PLANET_ID, 'personality');
  const dSun = find(activations, SUN_PLANET_ID, 'design');
  const pNode = find(activations, NORTH_NODE_PLANET_ID, 'personality');
  const dNode = find(activations, NORTH_NODE_PLANET_ID, 'design');
  return {
    determinationBase: baseValue(dSun, phsCategoryFor('design', 'incarnation'), 'detb'),
    environmentBase: baseValue(dNode, phsCategoryFor('design', 'life'), 'envb'),
    motivationBase: baseValue(pSun, phsCategoryFor('personality', 'incarnation'), 'motb'),
    perspectiveBase: baseValue(pNode, phsCategoryFor('personality', 'life'), 'perb'),
  };
}

/** Evaluator op basis van een render-state-factory (de component levert `HdBodygraphRenderStateFactory.create`). */
export function createBirthWindowEvaluator(createRenderState: HdBirthWindowRenderStateFactory): HdBirthWindowEvaluate {
  return createMemoizedBirthWindowEvaluator(activations => evaluateBirthWindowSnapshot(activations, createRenderState));
}

function listDiff(birth: readonly string[], current: readonly string[]): HdBirthWindowListDiff {
  const birthSet = new Set(birth);
  const currentSet = new Set(current);
  return {
    added: current.filter(item => !birthSet.has(item)),
    removed: birth.filter(item => !currentSet.has(item)),
    unchanged: current.filter(item => birthSet.has(item)),
  };
}

/** Index van het interval dat `birthMs` bevat (het laatste als `birthMs` precies op het einde valt). */
function birthIntervalIndex(intervals: readonly HdBirthWindowInterval[], birthMs: number): number {
  const index = intervals.findIndex(interval => birthMs >= interval.fromMs && birthMs < interval.toMs);
  if (index >= 0) return index;
  return intervals.length > 0 && birthMs === intervals[intervals.length - 1].toMs ? intervals.length - 1 : -1;
}

/**
 * Per eigenschap: opeenvolgende intervallen met dezelfde waarde-id smelten
 * samen tot één blok; de staat is relatief aan het blok dat de geboorte bevat.
 */
export function buildBirthWindowLanes(
  intervals: readonly HdBirthWindowInterval[],
  birthMs: number,
  evaluate: HdBirthWindowEvaluate,
): HdBirthWindowLane[] {
  if (intervals.length === 0) return [];
  const snapshots = intervals.map(interval => evaluate(interval.activations));
  const birthIndex = birthIntervalIndex(intervals, birthMs);

  return HD_BIRTH_WINDOW_PROPERTY_ORDER.map(key => ({
    key,
    tier: HD_BIRTH_WINDOW_TIER_OF[key],
    blocks: laneBlocks(key, intervals, snapshots, birthIndex),
  }));
}

function laneBlocks(
  key: HdBirthWindowPropertyKey,
  intervals: readonly HdBirthWindowInterval[],
  snapshots: readonly HdBirthWindowSnapshot[],
  birthIndex: number,
): HdBirthWindowBlock[] {
  const birthValue = birthIndex >= 0 ? snapshots[birthIndex][key] : undefined;
  const merged: { fromMs: number; toMs: number; value: HdBirthWindowPropertyValue; containsBirth: boolean }[] = [];

  intervals.forEach((interval, index) => {
    const value = snapshots[index][key];
    const last = merged[merged.length - 1];
    if (last && last.value.id === value.id && last.toMs === interval.fromMs) {
      last.toMs = interval.toMs;
      last.containsBirth = last.containsBirth || index === birthIndex;
      return;
    }
    merged.push({ fromMs: interval.fromMs, toMs: interval.toMs, value, containsBirth: index === birthIndex });
  });

  return merged.map(block => {
    const state = block.containsBirth ? 'birth' : birthValue && block.value.id === birthValue.id ? 'same' : 'different';
    const diff = birthValue && birthValue.payload.kind === 'list' && block.value.payload.kind === 'list' && !block.containsBirth
      ? listDiff(birthValue.payload.items, block.value.payload.items)
      : undefined;
    return diff ? { fromMs: block.fromMs, toMs: block.toMs, value: block.value, state, diff } : { fromMs: block.fromMs, toMs: block.toMs, value: block.value, state };
  });
}
